import styled, { css, keyframes } from 'styled-components'
import theme from '@app/ui/theme'

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const dotAnimation = keyframes`
  0%   { content: '.'; }
  33%  { content: '..'; }
  66%  { content: '...'; }
  100% { content: '.'; }
`

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: calc(400px + 100%) 0; }
`

export const HeaderInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`

export const Title = styled.span`
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: ${theme.colors.primary};
`

export const Subtitle = styled.span`
  font-size: 13px;
  line-height: 1.5;
  color: ${theme.colors.secondary};
`

export const Body = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px 20px 20px;
  overflow: hidden;
`

export const InputRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const InputField = styled.div<{
  $hasError?: boolean
  $isFocused?: boolean
}>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 6px 6px 6px 14px;
  border-radius: 12px;
  background: ${theme.colors.white};
  border: 1px solid
    ${({ $hasError, $isFocused }) =>
      $hasError
        ? '#f0cccc'
        : $isFocused
          ? theme.colors.secondary2
          : theme.colors.light};
  transition: border-color 120ms ease, box-shadow 120ms ease;
  ${({ $isFocused, $hasError }) =>
    $isFocused &&
    !$hasError &&
    css`
      box-shadow: 0 0 0 3px #edf4fd;
    `};
  ${({ $hasError }) =>
    $hasError &&
    css`
      box-shadow: 0 0 0 3px #fff2f2;
    `};
`

export const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  padding: 12px 0;
  background: transparent;
  /* 16px prevents iOS Safari from zooming the viewport on focus */
  font-size: 16px;
  color: ${theme.colors.primary};
  -webkit-appearance: none;
  appearance: none;

  &::placeholder {
    color: ${theme.colors.secondary};
  }
`

export const InviteButton = styled.button`
  border: none;
  border-radius: 10px;
  padding: 12px 18px;
  min-width: 112px;
  min-height: 44px;
  background: ${theme.colors.secondary2};
  color: ${theme.colors.white};
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: opacity 120ms ease, background 120ms ease;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const InlineError = styled.div`
  font-size: 13px;
  color: ${theme.colors.warning};
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 18px;
`

export const Hint = styled.div`
  font-size: 13px;
  color: ${theme.colors.secondary};
  min-height: 18px;
`

export const SectionLabel = styled.div`
  font-size: 12px;
  color: ${theme.colors.secondary};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
  margin-top: 4px;
`

export const UsersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 4px 2px 4px 0;
  flex: 1;
  min-height: 0;
`

export const UserItem = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid ${theme.colors.light};
  background: ${theme.colors.white};
  border-radius: 12px;
  transition: border-color 120ms ease, background 120ms ease;

  /* hover-only devices — avoids 'sticky hover' on touch */
  @media (hover: hover) {
    &:hover {
      border-color: ${theme.colors.light4};
      background: ${theme.colors.light3};
    }
  }
`

export const UserName = styled.div`
  color: ${theme.colors.primary};
  font-size: 15px;
  font-weight: 700;
`

export const UserEmail = styled.div`
  margin-top: 2px;
  color: ${theme.colors.secondary};
  font-size: 13px;
  word-break: break-all;
`

export const InvitedLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid #c5dfd2;
  background: #ebf7f0;
  color: ${theme.colors.accept};
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
`

export const NoResults = styled.div`
  text-align: center;
  padding: 28px 16px;
  color: ${theme.colors.secondary};
  border: 1px dashed ${theme.colors.light};
  border-radius: 12px;
  background: ${theme.colors.light3};
  font-size: 14px;
`

export const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 16px;
`

export const LoadingText = styled.span`
  font-family: 'Roboto', sans-serif;
  font-size: 15px;
  color: ${theme.colors.secondary2};
  font-weight: 600;
  &:after {
    content: '.';
    animation: ${dotAnimation} 1.5s steps(3, end) infinite;
  }
`

const shimmerBg = css`
  background: linear-gradient(
    90deg,
    ${theme.colors.light3} 0%,
    ${theme.colors.light2} 50%,
    ${theme.colors.light3} 100%
  );
  background-size: 400px 100%;
  background-repeat: no-repeat;
  animation: ${shimmer} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    background: ${theme.colors.light3};
  }
`

export const SkeletonRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid ${theme.colors.light};
  background: ${theme.colors.white};
  border-radius: 12px;
`

export const SkeletonText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const SkeletonBar = styled.div<{ $width?: string; $height?: number }>`
  width: ${({ $width = '60%' }) => $width};
  height: ${({ $height = 14 }) => $height}px;
  border-radius: 6px;
  ${shimmerBg};
`

export const SkeletonButton = styled.div`
  width: 112px;
  height: 44px;
  border-radius: 10px;
  ${shimmerBg};
`

export const Spinner = styled.span<{ $size?: number; $color?: string }>`
  display: inline-block;
  width: ${({ $size = 14 }) => $size}px;
  height: ${({ $size = 14 }) => $size}px;
  border-radius: 50%;
  border: 2px solid ${({ $color }) => $color || 'rgba(255,255,255,0.4)'};
  border-top-color: ${({ $color }) =>
    $color ? 'transparent' : theme.colors.white};
  animation: ${spin} 0.8s linear infinite;
`

// Kept for backwards compatibility — referenced in @app/navigation/index.tsx
export const SearchContainer = styled.div`
  padding: 16px;
  overflow-y: hidden;
`
